/**
 * THE PARTY RECORD — `/countries/:cc/parties/:partyId`, the customer's home page and the
 * screen OQ-PARTY-REFERRAL-WRITE §5 named as the missing half of the ruled write:
 * *"a party record page (route + query + layout), and on it, a `parties.create`-gated
 * one-field control calling `POST /parties/:id/referral`, mirroring `CrfRecordPage`'s
 * Customer-group row + write control."* This file is that cluster.
 *
 * The composition follows `CrfRecordPage` (breadcrumb header, main column, 320px
 * properties aside) minus what a party does not have: no `DocRail` and no stage pill
 * (a customer has no lifecycle tone), no tabs (three cards do not need a tab row), and
 * no money anywhere — `PartyDto` carries none, so nothing here branches on `'•••'`.
 *
 * ===========================================================================
 * THE TWO PANELS THAT WERE BUILT WAITING FOR THIS PAGE ARE MOUNTED, NOT REBUILT
 * ===========================================================================
 * `PartyCallHistoryPanel` (v20 D2) and `PartyLicenceTab` were both shipped as
 * props-driven components with headers saying the party record page did not exist yet
 * and that mounting them here would cost one line each. It did. Their routed homes
 * (`parties/:partyId/calls`, `parties/:partyId/licences`) STAY MOUNTED — deep links and
 * the report keep working, and retiring either page is an IA decision for the owner,
 * not this cluster. Each panel keeps its own permission behaviour: the licences panel
 * is ABSENT (not empty) without `quotes.view`, exactly as its header argues.
 *
 * ===========================================================================
 * "REFERRED BY" — THE ONE WRITE CONTROL, AND WHY IT CONFIRMS BEFORE OVERWRITING
 * ===========================================================================
 * The row mirrors `CrfRecordPage`'s `ReferredByRow` (E23) with ONE deliberate addition:
 * when a value already stands, Save opens a confirm naming it. The route OVERWRITES by
 * owner ruling, and the trail records what was destroyed — every write emits one
 * `party.referral_set` whose payload carries `previous_referral` (owner ruling
 * 2026-08-20: *"KEEP the overwrite, and make the trail carry the OLD VALUE as well as
 * who changed it"*). So the confirm says exactly what the trail will say: the value
 * being replaced, beside who replaced it. A FIRST set (nothing recorded yet) commits
 * without the confirm — there is no previous value to show, and the trail records
 * `previous_referral: null` for the same reason a dialog with nothing to name would be
 * ceremony. `party-record.test.tsx` pins both paths; `party-referral-screen.e2e-spec.ts`
 * holds the trail's half against the real API.
 *
 * D5 THROUGHOUT — the gate REMOVES actions, never facts: the VALUE renders for every
 * viewer of the record; the Set/Change control renders ONLY for a seat holding
 * `parties.create` in this country (the route's own key — deliberately NOT
 * `parties.link`, which binds money to a Tally ledger and is a different authority) and
 * is otherwise absent from the DOM. Not disabled, not tooltipped.
 *
 * ⚠ RULE 5 CALLOUT (AGT U3, 2026-09-26): "the ONE write control" holds outside SAF-T (AO) only.
 * In a `saftAo` country the Contact group also edits name, NIF, address and city
 * (`PartyDetailsForm` → `PATCH /parties/:id`, design §3 U3), under the same key and D5 rule.
 *
 * ===========================================================================
 * THE ACTIVITY TRAIL RENDERS THE VERB HONESTLY, WITHOUT A BESPOKE BUILDER
 * ===========================================================================
 * The feed is `GET /countries/:cc/events?entity=party&entityId=…` — country scope is
 * the only gate. `domain/events.ts` carries no builder for `party.referral_set` ON
 * PURPOSE (the `product.created` precedent: a verb without a bespoke renderer degrades
 * to the honest fallback sentence, and writing one is a product change this cluster
 * does not fold in). The row reads "Referral set" — present, dated, attributed — which
 * keeps the confirm's promise: the write lands on this very card.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import {
  Alert,
  Card,
  Chip,
  ConfirmModal,
  CountryBadge,
  SkeletonBlock,
} from '../../components/ui';
import { apiRequest } from '../../lib/api';
import type { Paged } from '../../lib/inquiries-api';
import type { CountryCode } from '../../domain/countries';
import { countryProfile, customerOriginLabel } from '../../domain/countries';
import {
  SAFT_ADDRESS_MAX,
  SAFT_CITY_MAX,
  SAFT_PHONE_MAX,
  SAFT_PROVINCE_MAX,
  SAFT_TAX_ID_MAX,
} from '../../domain/saft-fields';
import type { EventRow } from '../../domain/events';
import { PropertyGroup, PropertyRow } from '../inquiries/record-furniture';
import { formatDateLong } from '../inquiries/format';
import { InquiryActivityFeed } from '../inquiries/Timeline';
import { useToast } from '../inquiries/toast';
import { PartyCallHistoryPanel } from '../party-calls/PartyCallHistoryPanel';
import { PartyLicenceTab } from '../tally-licences/PartyLicenceTab';
import { fetchParty, partyKeys, setPartyReferral, updateParty } from './api';
import type { PartyDto, UpdatePartyBody } from './api';
import { partyErrorText } from './errors';

/* ───────────────────────────────────────────────────────────────── events ── */

const partyEventKeys = {
  forParty: (cc: string, id: string) => ['events', cc, 'party', id] as const,
};

/**
 * The country feed filtered to this customer — the same route and shape as
 * `useCrfEvents` on the CRF record, with `entity=party`. Country scope is the
 * only gate (spec §10): no permission beyond standing in the country.
 */
function usePartyEvents(countryCode: string, partyId: string): UseQueryResult<Paged<EventRow>> {
  return useQuery({
    queryKey: partyEventKeys.forParty(countryCode, partyId),
    queryFn: ({ signal }) =>
      apiRequest<Paged<EventRow>>(`/countries/${countryCode}/events`, {
        query: { entity: 'party', entityId: partyId, page: 1, perPage: 25 },
        signal,
      }),
  });
}

/* ───────────────────────────────────────────────────────────────── screen ── */

export function PartyRecordPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { partyId } = useParams<{ partyId: string }>();
  const { can } = useSession();

  const canView = can(countryCode, 'parties.view');
  const id = partyId ?? null;
  const record = useQuery({
    queryKey: partyKeys.record(id ?? ''),
    queryFn: ({ signal }) => fetchParty(id ?? '', signal),
    enabled: canView && id !== null,
  });

  if (!canView) {
    // The permission refusal is its own state with its own words — never the empty
    // state, never the error state (`PartyCallHistoryPanel`'s recorded rule).
    return (
      <div className="p-6">
        <Alert tone="danger" title="This customer is not available to this account">
          Opening customers in {countryCode} needs the parties view permission. Ask an
          administrator to widen your access.
        </Alert>
      </div>
    );
  }

  if (record.isPending) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <SkeletonBlock className="h-4 w-64" />
        <SkeletonBlock className="h-8 w-80" />
        <SkeletonBlock className="h-64 w-full" />
      </div>
    );
  }

  if (record.isError || record.data === undefined) {
    // A failed read is NOT a missing customer: the alert says what happened and the way
    // back; it never renders the record shell around a hole.
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="This customer could not be opened">
          {partyErrorText(record.error)}
        </Alert>
        <Link
          to={`/countries/${countryCode}/parties`}
          className="w-fit text-body13 text-fg underline underline-offset-2 hover:decoration-2"
        >
          Back to the customer register
        </Link>
      </div>
    );
  }

  return <PartyRecord key={record.data.id} countryCode={countryCode} party={record.data} />;
}

function PartyRecord({
  countryCode,
  party,
}: {
  /** Narrowed, not `string`: `can()` takes a `CountryCode` and this is the gate. */
  countryCode: CountryCode;
  party: PartyDto;
}): React.JSX.Element {
  const { can } = useSession();
  const queryClient = useQueryClient();

  /*
   * The ROUTE's key (`@RequirePerm('parties.create')` on `POST /parties/:id/referral`),
   * by owner ruling: "whoever may bring a customer into the system may record who sent
   * them." A Coordinator holds it; a Developer holds `parties.view` and does not —
   * which is the seat this screen shows the value to and offers no control
   * (role-matrix.json, measured in the e2e).
   */
  const canSetReferral = can(countryCode, 'parties.create');
  /*
   * Design §3 U3 (D13) — the edit of name, NIF, address and city: SAF-T (AO) countries only
   * (the route refuses others 422), on the route's own key. Absent otherwise (D5).
   */
  const profile = countryProfile(countryCode);
  const canEditDetails = profile.saftAo && canSetReferral;
  const [editingDetails, setEditingDetails] = useState(false);

  const events = usePartyEvents(countryCode, party.id);

  const settle = (): void => {
    void queryClient.invalidateQueries({ queryKey: partyKeys.record(party.id) });
    void queryClient.invalidateQueries({
      queryKey: partyEventKeys.forParty(countryCode, party.id),
    });
  };

  const origin = customerOriginLabel(party.customer_country);

  return (
    <div className="flex flex-col gap-4 p-6">
      <header className="flex flex-col gap-4">
        <p className="text-label text-fg-muted">
          <Link to={`/countries/${countryCode}/parties`} className="hover:underline">
            Parties
          </Link>
          {' / '}
          <span>{party.name}</span>
        </p>

        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <CountryBadge code={countryCode} />
          <h1 className="truncate text-h1 text-fg">{party.name}</h1>
          {/*
            BOTH chips render on EVERY record — the `WorkTypeBadge only={null}` rule: on
            one record there is no neighbouring row to supply the contrast, so an absent
            chip and "this build does not know" would look identical. The words are the
            server's own vocabulary (`PARTY_SOURCES` / `PARTY_STATUSES`), not a client
            rename.
          */}
          <Chip>
            <span data-testid="party-source-chip">{party.source}</span>
          </Chip>
          <Chip>
            <span data-testid="party-status-chip">{party.status}</span>
          </Chip>
          {/*
            PB-6 (v24 A1) — THE QUIET CHIP, and quiet is the specification: the
            importer flagged BOTH twins of a name-only legacy duplicate group,
            and this is the whole render — words on the record, no control, no
            dismiss, because there is NO write surface for the flag anywhere
            (legacy-filter.e2e-spec.ts case c2 is the wall). Unflagged records
            render nothing — a chip on every row is noise, not a flag.
          */}
          {party.possible_duplicate === true ? (
            <Chip>
              <span data-testid="party-duplicate-chip">possible duplicate</span>
            </Chip>
          ) : null}
        </div>
      </header>

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <div className="flex min-w-0 flex-1 flex-col gap-5">
          <Card
            title="Call history"
            action={
              <Link
                to={`/countries/${countryCode}/parties/${party.id}/calls`}
                className="text-label font-medium text-indigo hover:underline"
              >
                Full log
              </Link>
            }
          >
            <PartyCallHistoryPanel countryCode={countryCode} partyId={party.id} />
          </Card>

          {/* Renders its own card — or nothing at all without `quotes.view` (absent,
            * not empty; its header records why the difference is the point). */}
          <PartyLicenceTab countryCode={countryCode} partyId={party.id} partyName={party.name} />

          <Card title="Activity">
            {events.isPending ? <SkeletonBlock className="h-32 w-full" /> : null}
            {events.isError ? (
              <Alert tone="danger" title="The activity trail could not be loaded">
                <p>
                  A failed read is not an empty trail — what this customer&apos;s record
                  carries is unknown right now. {partyErrorText(events.error)}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    void events.refetch();
                  }}
                  className="mt-1 font-medium underline underline-offset-2"
                >
                  Try again.
                </button>
              </Alert>
            ) : null}
            {events.data === undefined ? null : <InquiryActivityFeed events={events.data.data} />}
          </Card>
        </div>

        {/* The 320px properties panel — `Record/PropertiesPanel`'s composition. */}
        <aside className="flex w-full shrink-0 flex-col gap-5 rounded-md border border-line bg-surface p-5 lg:w-[320px]">
          <PropertyGroup label="Customer">
            {/*
              * "REFERRED BY" LIVES IN THE CUSTOMER GROUP — the CRF record's recorded rule:
              * it is a fact about who brought the customer, not a contact detail and not a
              * system fact. `party-record.test.tsx` pins the group it is in AND the groups
              * it is not in.
              */}
            <ReferredByRow party={party} canWrite={canSetReferral} onSaved={settle} />
            {origin === null ? null : (
              /* §B3 — only XM rows carry it; for the five domestic scopes the question
               * does not arise, and an em-dash row would invite an answer nobody asked. */
              <PropertyRow name="Origin">{origin}</PropertyRow>
            )}
          </PropertyGroup>

          <PropertyGroup label="Contact">
            {editingDetails ? (
              <PartyDetailsForm
                party={party}
                taxLabel={profile.taxLabel}
                onClose={() => setEditingDetails(false)}
                onSaved={settle}
              />
            ) : (
              <>
                <PropertyRow name="Phone">{party.phone ?? <Quiet />}</PropertyRow>
                <PropertyRow name="Email">{party.email ?? <Quiet />}</PropertyRow>
                <PropertyRow name="Address">{party.address ?? <Quiet />}</PropertyRow>
                <PropertyRow name="City">{party.city ?? <Quiet />}</PropertyRow>
                <PropertyRow name="State">{party.state ?? <Quiet />}</PropertyRow>
                <PropertyRow name="Tax no">
                  {party.tax_no === null ? <Quiet /> : <span className="font-mono text-mono">{party.tax_no}</span>}
                </PropertyRow>
                {canEditDetails ? (
                  <button
                    type="button"
                    onClick={() => setEditingDetails(true)}
                    className="w-fit text-label font-medium text-indigo hover:underline"
                  >
                    Edit name, {profile.taxLabel}, address and city
                  </button>
                ) : null}
              </>
            )}
          </PropertyGroup>

          <PropertyGroup label="System">
            <PropertyRow name="Party ID" mono>
              {party.id}
            </PropertyRow>
            {/* NULL for a prospect is "the question does not arise" (no ledger yet),
              * so the row is ABSENT rather than dashed — the confirmation-group rule. */}
            {party.tally_guid === null ? null : (
              <PropertyRow name="Tally GUID" mono>
                {party.tally_guid}
              </PropertyRow>
            )}
            <PropertyRow name="Created">{formatDateLong(party.created_at)}</PropertyRow>
            <PropertyRow name="Updated">{formatDateLong(party.updated_at)}</PropertyRow>
          </PropertyGroup>
        </aside>
      </div>
    </div>
  );
}

/** An absent optional, quietly — the register's em-dash treatment. */
function Quiet(): React.JSX.Element {
  return <span className="text-fg-secondary">—</span>;
}

/* ─────────────────────────────────────────────────────────── referred by ── */

/**
 * The "Referred by" property row — the value for every viewer, and (for
 * `parties.create`) the ONE write control that reaches `POST /parties/:id/referral`.
 * `CrfRecordPage.ReferredByRow` copied line for line, then ONE addition: the overwrite
 * confirm (the file header records why, and why a first set skips it).
 *
 * THE WIRE — `{ referral: 'Shah & Co' }` SETS; a blank field sends `{ referral: null }`
 * and CLEARS (the key is mandatory on the route, so the two statements are spelled out
 * rather than left to omission). The response is the WHOLE party as the server now
 * holds it (trimmed, blank → NULL, NOTHING re-cased — what the operator typed is what
 * is stored, the owner's 2026-08-18 ruling), and it goes straight into
 * `partyKeys.record(id)` — which is the SAME cache entry the inquiry record's Customer
 * panel reads, so that panel agrees without a refetch. `onSaved` then invalidates the
 * record (a refetch that agrees) and the activity feed, which now carries one more
 * `party.referral_set` row.
 */
function ReferredByRow({
  party,
  canWrite,
  onSaved,
}: {
  party: PartyDto;
  canWrite: boolean;
  onSaved: () => void;
}): React.JSX.Element {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [value, setValue] = useState('');

  const current = party.referral ?? null;
  const trimmed = value.trim();
  /** What Save will send: the SET statement, or `null` — the CLEAR. */
  const next = trimmed === '' ? null : trimmed;

  const save = useMutation({
    // Computed from `value` at mutate time (the CRF row's shape), so the wire can never
    // disagree with what the input holds when Save lands.
    mutationFn: () => {
      const typed = value.trim();
      return setPartyReferral(party.id, { referral: typed === '' ? null : typed });
    },
    onSuccess: (record) => {
      // The response IS the record (the route returns the same view `GET /parties/:id`
      // returns) — put it in the cache as-is so the row shows the STORED value the
      // instant the form closes, with no flash of the old one.
      queryClient.setQueryData(partyKeys.record(party.id), record);
      setConfirming(false);
      setEditing(false);
      onSaved();
      toast.push({
        text:
          record.referral === null || record.referral === undefined
            ? 'Referred by cleared.'
            : `Referred by recorded: ${record.referral}.`,
      });
    },
    onError: (error) => {
      // Back to the form with the typed value intact — the server's sentence names the
      // rule it found, and the operator edits rather than retypes.
      setConfirming(false);
      toast.push({ text: partyErrorText(error), tone: 'error' });
    },
  });

  if (editing) {
    return (
      <>
        <form
          data-testid="party-referral-form"
          className="flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            /*
             * OVERWRITE AND CLEAR CONFIRM; A FIRST SET COMMITS. `current` is the value
             * the trail will name as `previous_referral` — when there is one, the
             * operator reads it before it is destroyed. When there is none, there is
             * nothing to name and the dialog would be ceremony (the file header).
             */
            if (current === null) {
              save.mutate();
            } else {
              setConfirming(true);
            }
          }}
        >
          <div className="flex items-start gap-2">
            <label
              htmlFor="party-referral-input"
              className="w-[104px] shrink-0 pt-1.5 text-label text-fg-muted"
            >
              Referred by
            </label>
            <input
              id="party-referral-input"
              type="text"
              value={value}
              maxLength={200}
              autoFocus
              placeholder="A person, a firm, a partner — or Direct"
              onChange={(event) => setValue(event.target.value)}
              className="h-8 min-w-0 flex-1 rounded-sm border border-line bg-surface px-2 text-body13 text-fg placeholder:text-fg-muted"
            />
          </div>
          <div className="flex items-center gap-3 pl-[112px]">
            <button
              type="submit"
              disabled={save.isPending}
              className="inline-flex h-7 items-center rounded-sm bg-indigo px-2.5 text-label font-medium text-fg-inverse hover:bg-indigo/90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="text-label text-fg-secondary hover:text-fg"
            >
              Cancel
            </button>
            <span className="text-label text-fg-muted">Leave it blank to clear.</span>
          </div>
        </form>
        {current === null ? null : (
          /*
           * THE CONFIRM NAMES ITS TARGET (`ConfirmDialog`'s recorded rule: "Are you
           * sure?" is not answerable) — and what it names is the PREVIOUS value,
           * because that is what the write destroys and exactly what the trail's
           * `previous_referral` will carry. The UI says what the trail will say.
           */
          <ConfirmModal
            open={confirming}
            title={
              next === null
                ? `Clear "${current}" from this customer?`
                : `Replace "${current}"?`
            }
            body={
              next === null
                ? `"${current}" stops being this customer's referral. The activity trail will record the removed value — "${current}" — beside who removed it.`
                : `"${current}" is overwritten by "${next}". The activity trail will record the replaced value — "${current}" — beside who replaced it; the new spelling lives on the record, not on the trail.`
            }
            confirmLabel={next === null ? 'Clear it' : 'Replace it'}
            busy={save.isPending}
            onConfirm={() => save.mutate()}
            onCancel={() => setConfirming(false)}
          />
        )}
      </>
    );
  }

  return (
    <PropertyRow name="Referred by">
      <span className="flex items-baseline gap-2">
        {current === null ? (
          <span className="text-fg-muted" data-testid="party-prop-referral">
            Not recorded
          </span>
        ) : (
          <span data-testid="party-prop-referral">{current}</span>
        )}
        {canWrite ? (
          /* D5 — ABSENT without `parties.create`, never disabled (see the header). */
          <button
            type="button"
            onClick={() => {
              setValue(current ?? '');
              setEditing(true);
            }}
            className="text-label font-medium text-indigo hover:underline"
          >
            {current === null ? 'Set' : 'Change'}
          </button>
        ) : null}
      </span>
    </PropertyRow>
  );
}

/* ─────────────────────────────────────────────── customer details (AGT U3) ── */

/**
 * `PATCH /parties/:id` — design §3 U3 (D13: "fill city by hand through it"). The fields (phone and
 * state since U6) with the XSD bounds as `maxLength`; only the CHANGED ones are sent, and blank sends `null`
 * (clears) for all but the name. The
 * server judges the AGT locks (409 with its own sentence, shown through `partyErrorText` like
 * the referral row's refusals) and leaves the form standing with what was typed.
 */
function PartyDetailsForm({
  party,
  taxLabel,
  onClose,
  onSaved,
}: {
  party: PartyDto;
  taxLabel: string;
  onClose: () => void;
  onSaved: () => void;
}): React.JSX.Element {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [name, setName] = useState(party.name);
  const [taxNo, setTaxNo] = useState(party.tax_no ?? '');
  const [address, setAddress] = useState(party.address ?? '');
  const [city, setCity] = useState(party.city ?? '');
  const [phone, setPhone] = useState(party.phone ?? '');
  const [state, setState] = useState(party.state ?? '');
  const orNull = (value: string): string | null => (value.trim() === '' ? null : value.trim());

  const save = useMutation({
    // Only what was changed: the server re-validates and re-judges every field it is sent, so an
    // untouched stored value over a bound (a long Tally address) would block a city-only edit.
    mutationFn: () => {
      const body: UpdatePartyBody = {};
      if (name.trim() !== party.name.trim()) body.name = name.trim();
      if (orNull(taxNo) !== orNull(party.tax_no ?? '')) body.tax_no = orNull(taxNo);
      if (orNull(address) !== orNull(party.address ?? '')) body.address = orNull(address);
      if (orNull(city) !== orNull(party.city ?? '')) body.city = orNull(city);
      if (orNull(phone) !== orNull(party.phone ?? '')) body.phone = orNull(phone);
      if (orNull(state) !== orNull(party.state ?? '')) body.state = orNull(state);
      return updateParty(party.id, body);
    },
    onSuccess: (record) => {
      queryClient.setQueryData(partyKeys.record(party.id), record);
      onClose();
      onSaved();
      toast.push({ text: 'Customer details saved.' });
    },
    onError: (error) => {
      toast.push({ text: partyErrorText(error), tone: 'error' });
    },
  });

  const fields: Array<[label: string, value: string, set: (v: string) => void, max: number]> = [
    ['Name', name, setName, 200],
    [taxLabel, taxNo, setTaxNo, SAFT_TAX_ID_MAX],
    ['Address', address, setAddress, SAFT_ADDRESS_MAX],
    ['City', city, setCity, SAFT_CITY_MAX],
    // U6 — the SAF-T (AO) export refuses a phone over 20 or a state over 50 (XSD Telephone / Province).
    ['Phone', phone, setPhone, SAFT_PHONE_MAX],
    ['State', state, setState, SAFT_PROVINCE_MAX],
  ];

  return (
    <form
      data-testid="party-details-form"
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      {fields.map(([label, value, set, max]) => (
        <label key={label} className="flex items-start gap-2">
          <span className="w-[104px] shrink-0 pt-1.5 text-label text-fg-muted">{label}</span>
          <input
            type="text"
            value={value}
            maxLength={max}
            onChange={(event) => set(event.target.value)}
            className="h-8 min-w-0 flex-1 rounded-sm border border-line bg-surface px-2 text-body13 text-fg"
          />
        </label>
      ))}
      <p className="text-label text-fg-muted">
        Once a document issued in the AGT série carries this customer, the {taxLabel} can only be
        added (where there was none, or 999999999) and the name changed only once there is one.
      </p>
      <div className="flex items-center gap-3 pl-[112px]">
        <button
          type="submit"
          disabled={save.isPending || name.trim() === ''}
          className="inline-flex h-7 items-center rounded-sm bg-indigo px-2.5 text-label font-medium text-fg-inverse hover:bg-indigo/90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={onClose} className="text-label text-fg-secondary hover:text-fg">
          Cancel
        </button>
      </div>
    </form>
  );
}
